import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail,
  Shield,
  CheckCircle,
  XCircle,
  Lock,
  Unlock,
  ArrowLeft,
  Plus,
  Minus,
} from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleUser } from "@fortawesome/free-solid-svg-icons";
import Cookies from "js-cookie";
import {
  GetAllUsers,
  AccessedRestrictedProjects,
  AssignProject,
  RestrictedProject,
} from "../../Services/UserService";
const Dashboard = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [draggingApp, setDraggingApp] = useState(null);
  const [dragOverList, setDragOverList] = useState(null);
  const [loading, setLoading] = useState(false);

  //  Fetch all users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = Cookies.get("cls_token");
        if (!token) {
          console.error("No token found in cookies");
          return;
        }

        const response = await GetAllUsers(token);
        console.log("Get All Users:", response);

        if (response && Array.isArray(response.userDetails)) {
          const formattedUsers = response.userDetails.map((user, index) => ({
            id: user.userUID || index,
            name: user.name || "N/A",
            email: user.email || "N/A",
            empCode: user.empCode || "N/A",
            doj: user.doj || "N/A",
            dob: user.dob || "N/A",
            profilePhoto: user.profilePhoto || "",
            status: user.status === true ? "Active" : "Inactive",
            role: "IT",
            allowedApps: [],
            restrictedApps: [],
          }));
          setUsers(formattedUsers);
        } else {
          console.error("Unexpected API structure:", response);
        }
      } catch (error) {
        console.error("Failed to fetch users:", error.message);
      }
    };

    fetchUsers();
  }, []);

  //  Fetch allowed/restricted projects for selected user
  useEffect(() => {
    if (!selectedUser?.id) return;

    const fetchProjects = async () => {
      try {
        const response = await AccessedRestrictedProjects(selectedUser.id);
        console.log("Fetched User Projects:", response);

        if (response?.status === "Success" && response.projects) {
          const accessible = response.projects.accessibleProjects || [];
          const nonAccessible = response.projects.nonAccessibleProjects || [];

          setSelectedUser((prev) => ({
            ...prev,
            allowedApps: accessible.map((p) => ({
              appUID: p.appUID,
              appName: p.appName,
            })),
            restrictedApps: nonAccessible.map((p) => ({
              appUID: p.appUID,
              appName: p.appName,
            })),
          }));
        } else {
          console.error("⚠️ Unexpected API structure:", response);
        }
      } catch (error) {
        console.error("❌ Failed to fetch user projects:", error);
      }
    };

    fetchProjects();
  }, [selectedUser?.id]);

  //  Move app locally between allowed/restricted
  const moveApp = (appObj, from) => {
    const updatedUser = { ...selectedUser };

    if (from === "allowed") {
      updatedUser.allowedApps = updatedUser.allowedApps.filter(
        (a) => a.appUID !== appObj.appUID
      );
      updatedUser.restrictedApps = [...updatedUser.restrictedApps, appObj];
    } else {
      updatedUser.restrictedApps = updatedUser.restrictedApps.filter(
        (a) => a.appUID !== appObj.appUID
      );
      updatedUser.allowedApps = [...updatedUser.allowedApps, appObj];
    }

    setSelectedUser(updatedUser);
  };

  //  Handle drag-drop
  const handleDrop = async (targetList) => {
    if (!draggingApp || !selectedUser?.id) return;

    const createdBy = Cookies.get("userUID") || 0;
    const userUID = selectedUser.id;
    const appUID = draggingApp.appUID;

    try {
      setLoading(true);

      // Case 1: dragged from allowed → dropped to restricted
      if (
        selectedUser.allowedApps.some((a) => a.appUID === draggingApp.appUID) &&
        targetList === "restricted"
      ) {
        console.log(" Calling RestrictedProject API...");
        const res = await RestrictedProject(userUID, appUID, createdBy);
        console.log("RestrictedProject API Response:", res);

        if (res?.status === "Success") {
          moveApp(draggingApp, "allowed");
          alert(`✅ ${draggingApp.appName} restricted successfully!`);
        } else {
          alert(" Failed to restrict project.");
        }
      }

      // Case 2: dragged from restricted → dropped to allowed
      else if (
        selectedUser.restrictedApps.some(
          (a) => a.appUID === draggingApp.appUID
        ) &&
        targetList === "allowed"
      ) {
        console.log(" Calling AssignProject API...");
        const res = await AssignProject(userUID, appUID, createdBy);
        console.log("AssignProject API Response:", res);

        if (res?.status === "Success") {
          moveApp(draggingApp, "restricted");
          alert(`✅ ${draggingApp.appName} assigned successfully!`);
        } else {
          alert(" Failed to assign project.");
        }
      }
    } catch (err) {
      console.error("Error during drag-drop operation:", err);
      alert(" Operation failed. Please try again.");
    } finally {
      setDraggingApp(null);
      setDragOverList(null);
      setLoading(false);
    }
  };

  //  Assign project API call
  const handleAssignProject = async (appObj) => {
    if (!selectedUser?.id) return;

    try {
      setLoading(true);
      const createdBy = Cookies.get("userUID") || 0;

      const payload = {
        userUID: selectedUser.id,
        appUID: appObj.appUID,
        createdBy,
      };

      console.log("Assigning project payload:", payload);
      const res = await AssignProject(
        payload.userUID,
        payload.appUID,
        payload.createdBy
      );
      console.log("AssignProject API Response:", res);

      if (res?.status === "Success") {
        moveApp(appObj, "restricted");
        alert(`✅ ${appObj.appName} assigned successfully!`);
      } else {
        alert("⚠️ Failed to assign project. Try again.");
      }
    } catch (err) {
      console.error("Error assigning project:", err);
      alert("❌ Error assigning project");
    } finally {
      setLoading(false);
    }
  };

  // Restricted Project API Call
  const handleRestrictedProject = async (appObj) => {
    if (!selectedUser?.id) return;

    try {
      setLoading(true);
      const createdBy = Cookies.get("userUID") || 0;

      const payload = {
        userUID: selectedUser.id,
        appUID: appObj.appUID,
        createdBy,
      };

      console.log("Restricted project payload:", payload);
      const res = await RestrictedProject(
        payload.userUID,
        payload.appUID,
        payload.createdBy
      );
      console.log("RestrictedProject API Response:", res);

      if (res?.status === "Success") {
        moveApp(appObj, "allowed");
        alert(`✅ ${appObj.appName} Restricted successfully!`);
      } else {
        alert("⚠️ Failed to Restricted project. Try again.");
      }
    } catch (err) {
      console.error("Error Restricting project:", err);
      alert("❌ Error Restricting project");
    } finally {
      setLoading(false);
    }
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
    exit: { opacity: 0, y: -20, transition: { duration: 0.3 } },
  };

  return (
    <div className="p-8 pt-2 min-h-screen bg-gray-50">
      <AnimatePresence mode="popLayout">
          <h1 className="text-2xl font-bold text-[#004687] mb-8 text-center">
              Centralized Login Dashboard
            </h1>
        <motion.div
          key="all-users"
          className={`grid ${
            selectedUser
              ? "grid-cols-1"
              : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          } gap-8`}
          layout
        >
          <AnimatePresence>
            {users.map((user) =>
              selectedUser?.id === user.id ? (
                // === EXPANDED CARD ===
                <motion.div
                  key={user.id}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.4, ease: "easeInOut" }}
                  className="col-span-1 bg-white rounded-2xl shadow-xl border border-gray-200 p-8 relative overflow-hidden"
                >
                  <button
                    onClick={() => setSelectedUser(null)}
                    className="absolute top-4 right-4 text-gray-500 hover:text-[#004687] transition"
                  >
                    ✕
                  </button>

                  <div className="flex flex-col md:flex-row items-center gap-8">
                    <FontAwesomeIcon
                      icon={faCircleUser}
                      className="text-[#004687] text-7xl"
                    />
                    <div>
                      <h2 className="text-2xl font-semibold text-[#004687]">
                        {user.name}
                      </h2>
                      <p className="text-gray-600 flex items-center gap-2 mt-2">
                        <Mail size={16} /> {user.email}
                      </p>
                      <p className="text-gray-700 flex items-center gap-2 mt-2">
                        <Shield size={16} /> {user.role}
                      </p>
                      <p
                        className={`mt-3 inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                          user.status === "Active"
                            ? "bg-green-100 text-green-600"
                            : "bg-red-100 text-red-600"
                        }`}
                      >
                        <CheckCircle size={14} /> {user.status}
                      </p>
                    </div>
                  </div>

                  {/* Applications Section */}
                  <motion.div
                    className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    {/* Allowed Apps */}
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                      <h3 className="text-lg font-semibold text-green-700 mb-3 flex items-center gap-2">
                        <Unlock size={18} /> Allowed Apps
                      </h3>
                      {selectedUser?.allowedApps?.length > 0 ? (
                        <ul className="space-y-2">
                          {selectedUser.allowedApps.map((app) => (
                            <motion.li
                              key={app.appUID}
                              layout
                              className="flex justify-between bg-white shadow-sm px-4 py-2 rounded-lg text-green-700"
                            >
                              {app.appName}
                              <Minus
                                size={16}
                                className="cursor-pointer hover:text-red-500"
                                onClick={() => handleRestrictedProject(app)}
                              />
                            </motion.li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-500 italic">
                          No allowed apps.
                        </p>
                      )}
                    </div>

                    {/* Restricted Apps */}
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                      <h3 className="text-lg font-semibold text-red-700 mb-3 flex items-center gap-2">
                        <Lock size={18} /> Restricted Apps
                      </h3>
                      {selectedUser?.restrictedApps?.length > 0 ? (
                        <ul className="space-y-2">
                          {selectedUser.restrictedApps.map((app) => (
                            <motion.li
                              key={app.appUID}
                              layout
                              className="flex justify-between bg-white shadow-sm px-4 py-2 rounded-lg text-red-700"
                            >
                              {app.appName}
                              <Plus
                                size={16}
                                className="cursor-pointer hover:text-green-600"
                                onClick={() => handleAssignProject(app)}
                              />
                            </motion.li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-500 italic">
                          No restricted apps.
                        </p>
                      )}
                    </div>
                  </motion.div>
                </motion.div>
              ) : !selectedUser ? (
                // === COLLAPSED CARD ===
                <motion.div
                  key={user.id}
                  layout
                  onClick={() => setSelectedUser(user)}
                  className="bg-white rounded-2xl shadow-md hover:shadow-2xl border border-gray-100 cursor-pointer overflow-hidden"
                  whileHover={{ scale: 1.03 }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <div className="h-2 bg-gradient-to-r from-[#004687] to-[#007BFF]" />
                  <div className="flex flex-col items-center p-6">
                    <FontAwesomeIcon
                      icon={faCircleUser}
                      className="text-[#004687] text-6xl mb-3"
                    />
                    <h2 className="text-lg font-semibold text-[#004687] text-center">
                      {user.name}
                    </h2>
                    <div className="flex items-center justify-center gap-2 text-gray-600 text-sm mt-1">
                      <Mail size={15} />
                      <span>{user.email}</span>
                    </div>
                    <div className="mt-3 flex items-center justify-center gap-2 text-gray-700 text-sm">
                      <Shield size={15} />
                      <span className="font-medium">{user.role}</span>
                    </div>
                    <div
                      className={`mt-3 inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                        user.status === "Active"
                          ? "bg-green-100 text-green-600"
                          : "bg-red-100 text-red-600"
                      }`}
                    >
                      <CheckCircle size={14} /> {user.status}
                    </div>
                  </div>
                </motion.div>
              ) : (
                // === FADE OUT OTHER CARDS WHEN ONE IS SELECTED ===
                <motion.div
                  key={user.id}
                  initial={{ opacity: 1 }}
                  animate={{ opacity: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                />
              )
            )}
          </AnimatePresence>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default Dashboard;
